import { getCookie } from './cookieUtils';
import { getItemLocalStorage } from './localStroge';

/**
 * Validates if the user has a valid token cookie and access token
 * @returns {boolean} - true if both token cookie and access token exist, false otherwise
 */
export const isTokenValid = () => {
  const tokenCookie = getCookie('token');
  const accessToken = getItemLocalStorage("accessToken");
  return !!(tokenCookie && accessToken);
};

/**
 * Checks if user appears to be authenticated but is missing the token cookie
 * @returns {boolean} - true if user should be logged out due to missing token cookie
 */
export const shouldLogoutDueToMissingToken = () => {
  const tokenCookie = getCookie('token');
  const accessToken = getItemLocalStorage("accessToken");
  return accessToken && !tokenCookie;
};

/**
 * Gets the current authentication status
 * @returns {Object} - Object containing authentication status and details
 */

export const getAuthStatus = () => {
  const tokenCookie = getCookie('token');
  const accessToken = getItemLocalStorage("accessToken");
  
  return {
    hasTokenCookie: !!tokenCookie,
    hasAccessToken: !!accessToken,
    isAuthenticated: !!(tokenCookie && accessToken),
    shouldLogout: accessToken && !tokenCookie
  };
};      

/**
 * Comprehensive token validation with detailed logging
 * @returns {Object} - Object containing validation result and details
 */
export const validateTokenComprehensive = () => {
  const tokenCookie = getCookie('token');
  const accessToken = getItemLocalStorage("accessToken");
  
  const result = {
    isValid: false,
    hasTokenCookie: !!tokenCookie,
    hasAccessToken: !!accessToken,
    shouldLogout: false,
    reason: '',
    timestamp: new Date().toISOString(),
    url: window.location.href
  };
  
  // Check if we're on a pre-login page (these should not trigger logout)
  const preLoginPaths = ['/','/login', '/register', '/signin', '/signinotp', '/forgot-password', '/create-new-password'];
  const currentPath = window.location.pathname;
  const isPreLoginPage = preLoginPaths.some(path => currentPath.includes(path));
  
  if (isPreLoginPage) {
    result.isValid = true;
    result.reason = 'On pre-login page, skipping token validation';
    console.log('Token validation: Skipping validation for pre-login page:', currentPath);
    return result;
  }
  
  if (!accessToken) {
    result.reason = 'No access token found in localStorage';
    return result;
  }
  
  if (!tokenCookie) {
    result.shouldLogout = true;
    result.reason = 'Access token exists but token cookie is missing - user must be logged out';
    console.warn('Token validation failed:', result);
    return result;
  }
  
  result.isValid = true;
  result.reason = 'Token validation successful';
  return result;
};

/**
 * Enhanced token validation with additional security checks
 * @returns {Object} - Enhanced validation result
 */
export const validateTokenEnhanced = () => {
  const basicValidation = validateTokenComprehensive();
  
  // Additional security checks can be added here
  // For example, checking token expiration, format validation, etc.
  
  return {
    ...basicValidation,
    enhanced: true,
    securityLevel: 'high'
  };
};

/**
 * Silent token validation that doesn't log warnings
 * Useful for background checks
 * @returns {Object} - Validation result without logging
 */
export const validateTokenSilent = () => {
  const tokenCookie = getCookie('token');
  const accessToken = getItemLocalStorage("accessToken");
  
  // Check if we're on a pre-login page (these should not trigger logout)
  const preLoginPaths = ['/', '/login', '/register', '/signin', '/signinotp', '/forgot-password', '/create-new-password'];
  const currentPath = window.location.pathname;
  const isPreLoginPage = preLoginPaths.some(path => currentPath.includes(path));
  
  if (isPreLoginPage) {
    console.log('Silent validation: Skipping validation for pre-login page:', currentPath);
    return {
      isValid: true,
      hasTokenCookie: !!tokenCookie,
      hasAccessToken: !!accessToken,
      shouldLogout: false
    };
  }
  
  return {
    isValid: !!(tokenCookie && accessToken),
    hasTokenCookie: !!tokenCookie,
    hasAccessToken: !!accessToken,
    shouldLogout: accessToken && !tokenCookie
  };
}; 